import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

interface DayIssue {
  day: number;
  dayName: string;
  requiredMinutes: number;
  availableMinutes: number;
  shortfall: number;
}

interface ValidationIssue {
  resourceType: string;
  resourceName: string;
  resourceId: number;
  requiredMinutes: number;
  availableMinutes: number;
  shortfall: number;
  dayIssues: DayIssue[];
  issueType: string;
  details?: string;
}

interface ValidationResult {
  isValid: boolean;
  issues: ValidationIssue[];
  warnings: string[];
}

@Component({
  selector: 'app-schedule-validation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
  ],
  templateUrl: './schedule-validation-dialog.component.html',
  styleUrls: ['./schedule-validation-dialog.component.scss']
})
export class ScheduleValidationDialogComponent implements OnInit {
  loading = false;
  error: string | null = null;
  validationResult: ValidationResult | null = null;

  private apiBaseUrl = environment.apiBaseUrl;

  constructor(
    public dialogRef: MatDialogRef<ScheduleValidationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { scheduleId: number; organisationId: number },
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.validateSchedule();
  }

  validateSchedule() {
    this.loading = true;
    this.error = null;

    this.http.get<ValidationResult>(
      `${this.apiBaseUrl}/organisations/${this.data.organisationId}/schedules/${this.data.scheduleId}/validate`
    ).subscribe({
      next: (result) => {
        this.validationResult = result;
        this.loading = false;
      },
      error: (err) => {
        console.error('Validation error:', err);
        this.error = err.error?.error || 'Failed to validate schedule. Please try again.';
        this.loading = false;
      }
    });
  }

  formatMinutes(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  }

  getIssueTypeLabel(issueType: string): string {
    const labels: { [key: string]: string } = {
      'insufficient_capacity': 'Insufficient Capacity',
      'insufficient_day_distribution': 'Insufficient Day Distribution',
      'insufficient_time_overlap': 'Insufficient Time Overlap',
      'concurrent_capacity_exceeded': 'Concurrent Capacity Exceeded',
      'max_concurrent_exceeded': 'Max Concurrent Limit Exceeded'
    };
    return labels[issueType] || issueType;
  }

  getIssueTypeClass(issueType: string): string {
    const classes: { [key: string]: string } = {
      'insufficient_capacity': 'issue-type-capacity',
      'insufficient_day_distribution': 'issue-type-distribution',
      'insufficient_time_overlap': 'issue-type-overlap',
      'concurrent_capacity_exceeded': 'issue-type-concurrent',
      'max_concurrent_exceeded': 'issue-type-max-concurrent'
    };
    return classes[issueType] || 'issue-type-default';
  }

  close() {
    this.dialogRef.close();
  }
}
